import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  TextField,
  InputAdornment,
  Stack,
  Chip,
  Pagination,
} from '@mui/material';
import { Assignment, AccessTime, EmojiEvents, Schedule, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { StandaloneExam } from '../types';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Breadcrumb from '../components/Common/Breadcrumb';

const PAGE_SIZE = 12;

const StandaloneExams: React.FC = () => {
  const navigate = useNavigate();
  const [exams, setExams] = useState<StandaloneExam[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const fetchExams = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getPublishedStandaloneExams(currentPage, PAGE_SIZE, {
        search: appliedSearch || undefined,
      });
      setExams(data.results);
      setTotalCount(data.count);
      setTotalPages(data.total_pages);
    } catch (error) {
      console.error('Error fetching standalone exams:', error);
      setExams([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, appliedSearch]);

  useEffect(() => {
    fetchExams();
  }, [fetchExams]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(searchTerm);
    setCurrentPage(1);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) return <LoadingSpinner message="Loading exams..." />;

  return (
    <Box sx={{ py: 8 }}>
      <Container maxWidth="lg">
        <Breadcrumb
          items={[
            { label: 'Home', path: '/' },
            { label: 'Exams', path: '/exams' },
          ]}
        />

        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Standalone Exams
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Test your skills with exam-only assessments. Purchase access and take the exam when you're ready.
          </Typography>
        </Box>

        {/* Search Section */}
        <Box sx={{ mb: 6 }}>
          <Card sx={{ p: 4, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
            <Stack
              component="form"
              onSubmit={handleSearchSubmit}
              direction={{ xs: 'column', md: 'row' }}
              spacing={3}
              alignItems="center"
            >
              <Box sx={{ flex: 1, minWidth: '250px' }}>
                <TextField
                  fullWidth
                  placeholder="Search exams..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: '#10b981' }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#10b981',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#10b981',
                      },
                    },
                  }}
                />
              </Box>
            </Stack>
          </Card>
        </Box>

        {/* Results count */}
        {totalCount > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {totalCount} exam{totalCount !== 1 ? 's' : ''} found
          </Typography>
        )}

        {exams.length === 0 ? (
          <Card sx={{ p: 6, borderRadius: 3, textAlign: 'center' }}>
            <Assignment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No exams available at the moment.
            </Typography>
          </Card>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
            {exams.map((exam) => (
              <Box key={exam.id} sx={{ flex: '1 1 350px', maxWidth: '400px' }}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: exam.coming_soon ? 'default' : 'pointer',
                    transition: 'all 0.3s ease',
                    border: exam.coming_soon
                      ? '2px solid rgba(251, 191, 36, 0.5)'
                      : '1px solid rgba(0,0,0,0.08)',
                    position: 'relative',
                    overflow: 'hidden',
                    ...(!exam.coming_soon && {
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                      },
                    }),
                  }}
                  onClick={() => !exam.coming_soon && navigate(`/exams/${exam.hashed}`)}
                >
                  <Box sx={{ position: 'relative' }}>
                    {exam.thumbnail ? (
                      <CardMedia
                        component="img"
                        height="200"
                        image={exam.thumbnail}
                        alt={exam.title}
                        sx={{
                          objectFit: 'cover',
                          filter: exam.coming_soon ? 'grayscale(30%) brightness(0.9)' : 'none',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 200,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                          color: 'rgba(255,255,255,0.6)',
                          filter: exam.coming_soon ? 'grayscale(30%) brightness(0.9)' : 'none',
                        }}
                      >
                        <Assignment sx={{ fontSize: 80 }} />
                      </Box>
                    )}
                    {exam.coming_soon && (
                      <>
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background:
                              'linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.15) 100%)',
                            backdropFilter: 'blur(1px)',
                          }}
                        />
                        <Chip
                          icon={<Schedule sx={{ fontSize: '0.9rem' }} />}
                          label="COMING SOON"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '0.65rem',
                            letterSpacing: '0.5px',
                            boxShadow: '0 4px 20px rgba(251, 191, 36, 0.4)',
                          }}
                        />
                      </>
                    )}
                  </Box>
                  <CardContent
                    sx={{
                      flexGrow: 1,
                      px: 2,
                      pt: 1.25,
                      pb: 2.25,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.25,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '36px' }}>
                      <Typography
                        variant="body1"
                        component="h3"
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          lineHeight: 1.1,
                          flex: 1,
                          pr: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          opacity: exam.coming_soon ? 0.9 : 1,
                        }}
                      >
                        {exam.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 700,
                          color: exam.coming_soon ? '#fbbf24' : '#10b981',
                          fontSize: '1rem',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                      >
                        {exam.is_free ? 'Free' : `$${exam.price}`}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        rowGap: 0.75,
                        columnGap: 0.75,
                        alignItems: 'center',
                        mt: 0.5,
                        mb: 1,
                      }}
                    >
                      {exam.duration_minutes != null && (
                        <Chip
                          icon={<AccessTime sx={{ fontSize: '1rem' }} />}
                          label={`${exam.duration_minutes} min`}
                          size="small"
                          sx={{ borderRadius: 2, height: 24 }}
                        />
                      )}
                      {exam.grants_certificate && (
                        <Chip
                          icon={<EmojiEvents sx={{ fontSize: '1rem' }} />}
                          label="Certificate"
                          size="small"
                          color="primary"
                          sx={{ borderRadius: 2, height: 24 }}
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        )}

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default StandaloneExams;
